/** The shared filter rules every list screen uses (spec
 *  docs/superpowers/specs/2026-09-26-shared-list-filters-design.md).
 *
 *  ⚠️ ONE RULE PER QUESTION. Five screens hand-built these and disagreed on
 *  what "all" is (`'all'`, `''`, `null`) and on where an undated row goes. An
 *  empty selection means ALL here, and a null value is a real, selectable
 *  option, so an undated or unplaced game never vanishes from every setting. */

import { compareNatural } from './naturalOrder'

/** The option value standing in for a row whose facet value is null. */
export const MISSING_VALUE = '__none__'

export interface Facet<Row> {
  /** URL param name. */
  key: string
  /** "Division": the control's label. */
  label: string
  /** "divisions": used in "All divisions" and "3 divisions". */
  plural: string
  value: (row: Row) => string | null
  optionLabel?: (value: string) => string
  /** Label for MISSING_VALUE. Default `${label} TBD`. */
  missingLabel?: string
  /** Option order. Default `compareNatural`. */
  order?: (a: string, b: string) => number
  /** Rebuild this facet from the rows a screen is actually listing. `listView`
   *  calls it with the rows left after hiding past weeks, so a label derived
   *  from the rows (the week facet's earliest date) never names a hidden row. */
  rebuild?: (rows: readonly Row[]) => Facet<Row>
}

export interface FacetOption {
  value: string
  label: string
  /** Selected, but no loaded row carries it (an old link). */
  stale: boolean
}

export type Selection = Readonly<Record<string, readonly string[]>>

const valueOf = <Row>(facet: Facet<Row>, row: Row): string => facet.value(row) ?? MISSING_VALUE

function labelOf<Row>(facet: Facet<Row>, value: string): string {
  if (value === MISSING_VALUE) return facet.missingLabel ?? `${facet.label} TBD`
  return facet.optionLabel ? facet.optionLabel(value) : value
}

/** PURE. The facet's options from the loaded rows, the missing value last,
 *  then any selected value no row carries (labeled, never dropped). */
export function facetOptions<Row>(rows: readonly Row[], facet: Facet<Row>, selected: readonly string[] = []): FacetOption[] {
  const present = new Set(rows.map((r) => valueOf(facet, r)))
  const real = [...present].filter((v) => v !== MISSING_VALUE).sort(facet.order ?? compareNatural)
  const values = present.has(MISSING_VALUE) ? [...real, MISSING_VALUE] : real
  const stale = selected.filter((v) => !present.has(v))
  return [
    ...values.map((value) => ({ value, label: labelOf(facet, value), stale: false })),
    ...stale.map((value) => ({ value, label: `${labelOf(facet, value)} (no games)`, stale: true })),
  ]
}

/** PURE. OR within a facet, AND across facets; an empty pick passes. */
export function matchesSelection<Row>(row: Row, facets: readonly Facet<Row>[], selection: Selection): boolean {
  return facets.every((f) => {
    const picked = selection[f.key] ?? []
    return picked.length === 0 || picked.includes(valueOf(f, row))
  })
}

export function applyFilters<Row>(rows: readonly Row[], facets: readonly Facet<Row>[], selection: Selection): Row[] {
  return rows.filter((r) => matchesSelection(r, facets, selection))
}

// ── URL state ──────────────────────────────────────────────────────────────

export const SORT_PRESET_IDS = ['datetime', 'division', 'field', 'time', 'role', 'activity', 'team', 'week-division', 'week-field'] as const
export type SortPresetId = (typeof SORT_PRESET_IDS)[number]

export type ListSearch = Record<string, string>

export interface ListConfig {
  facets: readonly string[]
  /** Offered presets, the default first. Empty: the screen has no sort control. */
  sorts: readonly SortPresetId[]
  /** The sort a missing `sort` param means. Absent: `sorts[0]`. `null`: no
   *  preset, the rows keep the order the screen received them in. */
  defaultSort?: SortPresetId | null
  /** Absent: the screen has no Hide past weeks toggle. */
  hidePastDefault?: boolean
}

/** A list's config with its facet keys taken from the facet objects, so a
 *  key typed twice cannot drift (a typo used to filter nothing silently). */
export function listConfig(facets: readonly { key: string }[], rest: Omit<ListConfig, 'facets'>): ListConfig {
  return { facets: facets.map((f) => f.key), ...rest }
}

function defaultSortOf(config: ListConfig): SortPresetId | null {
  return config.defaultSort !== undefined ? config.defaultSort : config.sorts[0] ?? null
}

export interface ListState {
  selection: Record<string, string[]>
  sort: SortPresetId | null
  hidePast: boolean
}

function searchScalar(v: unknown): string | null {
  if (typeof v === 'string') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return null
}

/** Route `validateSearch`: keep string values, stringify numbers, drop the rest.
 *  A repeated key (`?week=3&week=4`) arrives as an array and is joined into one
 *  comma list, so it reads the same as `week=3,4`. */
export function validateListSearch(raw: Record<string, unknown>): ListSearch {
  const out: ListSearch = {}
  for (const [k, v] of Object.entries(raw)) {
    const value = Array.isArray(v)
      ? v.map(searchScalar).filter((x): x is string => x !== null).join(',') || null
      : searchScalar(v)
    if (value !== null) out[k] = value
  }
  return out
}

function decodeList(raw: string | undefined): string[] {
  if (!raw) return []
  const values = raw.split(',').map((s) => {
    try { return decodeURIComponent(s).trim() } catch { return '' }
  })
  return [...new Set(values.filter(Boolean))]
}

export function parseListSearch(search: Readonly<Record<string, string | undefined>>, config: ListConfig): ListState {
  const selection: Record<string, string[]> = {}
  for (const key of config.facets) selection[key] = decodeList(search[key])
  const asked = config.sorts.find((s) => s === search.sort)
  const hidePast = config.hidePastDefault === undefined
    ? false
    : search.past === 'show' ? false : search.past === 'hide' ? true : config.hidePastDefault
  return { selection, sort: asked ?? defaultSortOf(config), hidePast }
}

/** Only non-default values are written, so the bare route is the default view. */
export function serializeListSearch(state: ListState, config: ListConfig): ListSearch {
  const out: ListSearch = {}
  for (const key of config.facets) {
    const picked = state.selection[key] ?? []
    if (picked.length) out[key] = picked.map(encodeURIComponent).join(',')
  }
  if (state.sort && state.sort !== defaultSortOf(config)) out.sort = state.sort
  if (config.hidePastDefault !== undefined && state.hidePast !== config.hidePastDefault) {
    out.past = state.hidePast ? 'hide' : 'show'
  }
  return out
}

export function isDefaultListState(state: ListState, config: ListConfig): boolean {
  return Object.keys(serializeListSearch(state, config)).length === 0
}
